import { useState, useEffect } from "react";
import { Star, MessageSquareIcon, XIcon, CheckCheckIcon } from "lucide-react";
import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { Button } from "../../../Components/Buttons";
import { ToastMessage } from "../../../Components/Utils";
import { handleError } from "../../../stores/components/handleError";
import { useFetchAllReviewsQuery, useReplyReviewMutation } from "../../../stores/api/reviews.api";
import payloadHelper, { buildJson } from "../../../stores/api/helpers/payload.helper";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const StarDisplay = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={12}
        className={n <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-200 dark:text-gray-600"}
      />
    ))}
  </div>
);

const HEADERS = [
  { key: "id", label: "#" },
  { key: "first_image", label: "Photo", type: "image", imageBaseUrl: "", sortable: false },
  { key: "customer_name", label: "Customer" },
  { key: "rating_stars", label: "Rating", sortable: false },
  { key: "description", label: "Review", sortable: false },
  { key: "date", label: "Date", sortable: false },
  { key: "status", label: "Status", type: "status" },
];

const ReplyModal = ({ row, onClose, onReply, sending }) => {
  const [reply, setReply] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setReply("");
    setSent(Boolean(row?.replied_at || row?.reply_message));
  }, [row?.id, row?.replied_at, row?.reply_message]);

  if (!row) return null;

  const handleSend = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    try {
      await onReply(row.id, reply.trim());
      setSent(true);
      setReply("");
    } catch {
      // handled upstream
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-gray-100 dark:border-slate-700 p-6 flex flex-col gap-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-slate-800 dark:hover:text-gray-200 transition">
          <XIcon size={16} />
        </button>

        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-orange-100 dark:bg-orange-500/10 flex items-center justify-center flex-shrink-0">
            <MessageSquareIcon size={16} className="text-orange-500" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">{row.customer_name}</p>
          </div>
        </div>

        <div className="rounded-lg bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 p-4 space-y-2">
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{row.description}</p>
        </div>

        {row.reply_message && (
          <div className="rounded-lg bg-orange-50 dark:bg-orange-500/10 border border-orange-100 dark:border-orange-500/20 p-3 space-y-1">
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Previous Reply</p>
            <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">{row.reply_message}</p>
          </div>
        )}

        {sent ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-green-600 dark:text-green-400 font-medium">Reply sent to {row.customer_name}</p>
            <button type="button" onClick={onClose} className="px-4 py-1.5 text-xs font-semibold rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600 transition">Close</button>
          </div>
        ) : (
          <form onSubmit={handleSend} className="flex flex-col gap-3">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Reply to {row.customer_name}</label>
            <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Type your reply..." className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none transition" />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-4 py-1.5 text-xs font-semibold rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600 transition">Cancel</button>
              <button type="submit" disabled={sending || !reply.trim()} className="px-4 py-1.5 text-xs font-semibold rounded-md bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                {sending ? "Sending…" : "Send Reply"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const ReviewsList = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selected, setSelected] = useState(null);

  const queryParams = payloadHelper.forList({
    page,
    limit,
    search,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });

  const { data: raw = EMPTY_PAGINATION, isFetching } = useFetchAllReviewsQuery(queryParams);
  const [replyReview, { isLoading: sending }] = useReplyReviewMutation();

  const reviews = {
    ...raw,
    data: (raw.data ?? []).map((r) => ({
      ...r,
      first_image: r.images?.[0] ?? "",
      rating_stars: "★".repeat(r.rating) + "☆".repeat(5 - r.rating),
      date: new Date(r.createdAt).toLocaleDateString(),
    })),
  };

  const resetPage = () => setPage(1);

  const handleReply = async (id, replyText) => {
    try {
      await replyReview({ id, body: buildJson({ reply_message: replyText }) }).unwrap();
      ToastMessage({ type: "success", title: "Sent!", message: "Reply sent." });
    } catch (err) {
      handleError(err, "Failed to send reply.");
      throw err;
    }
  };

  return (
    <>
      <BreadCrumb title="Reviews" />
      <TableListing
        headers={HEADERS}
        paginationData={reviews}
        loading={isFetching}
        statusModule="review"
        search={search}
        setSearch={setSearch}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => { setLimit(value); resetPage(); }}
        onSearch={(value) => { setSearch(value); resetPage(); }}
        onSortChange={(sort) => { setSortConfig(sort); resetPage(); }}
        actions={(row) => (
          <div className="flex gap-2">
            <Button
              onClick={() => setSelected(row)}
              title={row.reply_message ? "Replied" : "Reply"}
              icon={row.reply_message ? CheckCheckIcon : MessageSquareIcon}
              className={
                row.reply_message
                  ? "text-green-500 hover:bg-green-50 hover:text-green-600 dark:hover:bg-green-500/10"
                  : "text-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-500/10"
              }
            />
          </div>
        )}
      />
      <ReplyModal row={selected} onClose={() => setSelected(null)} onReply={handleReply} sending={sending} />
    </>
  );
};

export default ReviewsList;
